// src/core/errors/classes/ConflictError.ts

/**
 * Conflict Error Class
 * WHY: 409 errors are a DISTINCT category from auth errors
 *
 * WHAT IS A 409 CONFLICT:
 * The request conflicts with the current state of the server.
 * This is NOT an auth issue - it's a data/state issue.
 *
 * COMMON 409 SCENARIOS:
 * ┌────────────────────────────────────────────────────────┐
 * │ Scenario              │ Example                       │
 * ├────────────────────────────────────────────────────────┤
 * │ Duplicate resource    │ Email already registered      │
 * │ Version conflict      │ Editing stale data (OCC)      │
 * │ State conflict        │ Cancelling already shipped    │
 * │ Unique constraint     │ Username already taken        │
 * │ Concurrent update     │ Two users editing same record │
 * └────────────────────────────────────────────────────────┘
 *
 * WHY NOT USE ApiError DIRECTLY:
 * ConflictError provides:
 * - conflictField: Which field caused the conflict
 * - conflictValue: What value conflicted
 * - existingResourceId: ID of the conflicting resource
 * - Resolution hints for the UI
 */

import { AppError, type AppErrorOptions } from "./base/AppError";
import { ErrorCategory, ErrorSeverity } from "../types";
import { ERROR_CODES } from "../constants";

export interface ConflictErrorOptions extends Omit<
  AppErrorOptions,
  "statusCode"
> {
  /** The field that caused the conflict (e.g., "email", "username") */
  conflictField?: string;
  /** The value that conflicted */
  conflictValue?: string;
  /** ID of the existing resource that conflicts */
  existingResourceId?: string;
  /** The type of resource (e.g., "User", "Product") */
  resourceType?: string;
}

export class ConflictError extends AppError {
  public readonly conflictField?: string;
  public readonly conflictValue?: string;
  public readonly existingResourceId?: string;
  public readonly resourceType?: string;

  constructor(
    message: string = "Resource already exists",
    options: ConflictErrorOptions = {},
  ) {
    super(message, {
      statusCode: 409,
      code: options.code ?? ERROR_CODES.CLIENT_CONFLICT,
      category: ErrorCategory.VALIDATION, // ✅ It's a VALIDATION issue, not AUTH
      severity: options.severity ?? ErrorSeverity.LOW,
      isOperational: true,
      isRetryable: false, // Retrying with same data won't help
      context: options.context,
      validationErrors: options.validationErrors,
      cause: options.cause,
    });

    this.conflictField = options.conflictField;
    this.conflictValue = options.conflictValue;
    this.existingResourceId = options.existingResourceId;
    this.resourceType = options.resourceType;
  }

  /**
   * Factory: Create from common duplicate scenarios
   */
  static duplicateEmail(email: string, cause?: unknown): ConflictError {
    return new ConflictError(`The email "${email}" is already registered`, {
      conflictField: "email",
      conflictValue: email,
      resourceType: "User",
      cause,
      validationErrors: [
        {
          field: "email",
          message: "This email is already registered. Try logging in instead.",
        },
      ],
    });
  }

  static duplicateUsername(username: string, cause?: unknown): ConflictError {
    return new ConflictError(`The username "${username}" is already taken`, {
      conflictField: "username",
      conflictValue: username,
      resourceType: "User",
      cause,
      validationErrors: [
        {
          field: "username",
          message: "This username is already taken. Please choose another.",
        },
      ],
    });
  }

  static duplicateResource(
    resourceType: string,
    field: string,
    value: string,
    cause?: unknown,
  ): ConflictError {
    return new ConflictError(
      `A ${resourceType} with ${field} "${value}" already exists`,
      {
        conflictField: field,
        conflictValue: value,
        resourceType,
        cause,
        validationErrors: [
          {
            field,
            message: `This ${field} is already in use.`,
          },
        ],
      },
    );
  }

  static versionConflict(resourceType: string, cause?: unknown): ConflictError {
    return new ConflictError(
      `This ${resourceType} has been modified by another user. Please refresh and try again.`,
      {
        resourceType,
        cause,
        // Version conflicts ARE retryable (after refresh)
        ...({ isRetryable: true } as any),
      },
    );
  }

  static stateConflict(message: string, cause?: unknown): ConflictError {
    return new ConflictError(message, { cause });
  }

  /**
   * Get user-friendly resolution hint
   */
  public getResolutionHint(): string {
    if (this.conflictField === "email") {
      return "This email is already registered. Would you like to log in instead?";
    }
    if (this.conflictField === "username") {
      return "This username is taken. Please choose a different one.";
    }
    if (this.conflictField) {
      return `The ${this.conflictField} "${this.conflictValue}" is already in use.`;
    }
    return "This resource already exists. Please modify your request.";
  }

  /**
   * Check if this is a duplicate resource conflict
   */
  public isDuplicateConflict(): boolean {
    return !!this.conflictField;
  }

  /**
   * Check if this is a version/state conflict
   */
  public isVersionConflict(): boolean {
    return !this.conflictField && this.isRetryable;
  }
}
